import { ADMIN_DATE_PLACEHOLDER, formatAdminDate } from './adminUserDisplay.js';

export { ADMIN_DATE_PLACEHOLDER, formatAdminDate };

const MONEY_KEYS = new Set([
  'askingPrice',
  'purchasePrice',
  'municipalEvaluation',
  'annualRent',
  'parkingRevenue',
  'internetRevenue',
  'storageRevenue',
  'otherRevenue',
  'municipalTaxes',
  'schoolTaxes',
  'insurance',
  'electricityHeating',
  'heating',
  'electricity',
  'maintenance',
  'concierge',
  'operatingExpenses',
  'otherExpenses',
  'assumedLoanBalance',
  'assumptionStatedMonthlyPayment',
  'netOperatingIncome',
  'cashFlow',
  'maxLoanAmount',
  'downPayment',
  'marketValue',
  'notary',
  'welcomeTax',
  'cmhcAnalysis',
  'cmhcTax',
  'expertises',
  'inspection',
  'appraiser',
  'renovations',
  'otherFees',
  'otherTests',
  'environmental1',
  'environmental2',
  'environmental3',
  'workCost',
]);

/** Values already stored as 5.5 meaning 5.5% — never multiply by 100. */
const PERCENT_KEYS = new Set([
  'vacancyRate',
  'managementRate',
  'mortgageRate',
  'qualificationRate',
  'financingCapRate',
  'capRate',
  'aphPoints',
  'ltvRatio',
  'revenueGrowthPct',
  'expenseGrowthPct',
  'valueAppreciationPct',
]);

function trimText(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

/**
 * Presentation-only. Backend already redacts financials for support.
 * @param {string | null | undefined} role
 */
export function canViewPropertyFinancials(role) {
  return role === 'admin' || role === 'super_admin';
}

/**
 * Join address parts without duplicating city/province/postal already in address.
 * @param {{ address?: unknown, city?: unknown, province?: unknown, postalCode?: unknown } | null | undefined} property
 */
export function formatAdminPropertyAddress(property) {
  if (!property || typeof property !== 'object') return '';
  const address = trimText(property.address);
  const extras = [property.city, property.province, property.postalCode]
    .map((part) => trimText(part))
    .filter(Boolean);
  if (!address && extras.length === 0) return '';
  if (!address) return extras.join(', ');
  const lower = address.toLowerCase();
  const missing = extras.filter((part) => !lower.includes(part.toLowerCase()));
  return missing.length ? `${address}, ${missing.join(', ')}` : address;
}

/**
 * @param {{ displayName?: unknown, email?: unknown, uid?: unknown } | null | undefined} owner
 */
export function adminOwnerPrimaryLabel(owner) {
  const name = trimText(owner?.displayName);
  if (name) return name;
  const email = trimText(owner?.email);
  if (email) return email;
  const uid = trimText(owner?.uid);
  return uid || ADMIN_DATE_PLACEHOLDER;
}

/**
 * List price: purchasePrice if present, else askingPrice.
 * @param {{ purchasePrice?: unknown, askingPrice?: unknown } | null | undefined} item
 * @returns {{ value: unknown, kind: 'purchase' | 'asking' | null }}
 */
export function adminPropertyListPrice(item) {
  if (!item || typeof item !== 'object') return { value: null, kind: null };
  if (item.purchasePrice != null && item.purchasePrice !== '') {
    return { value: item.purchasePrice, kind: 'purchase' };
  }
  if (item.askingPrice != null && item.askingPrice !== '') {
    return { value: item.askingPrice, kind: 'asking' };
  }
  return { value: null, kind: null };
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
export function parseAdminFiniteNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * CAD when the value is a finite number. Unknown non-numeric strings are shown as-is.
 * @param {unknown} value
 * @param {string} [language]
 */
export function formatAdminMoney(value, language = 'fr') {
  if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
  const n = parseAdminFiniteNumber(value);
  if (n == null) return typeof value === 'string' ? value.trim() : ADMIN_DATE_PLACEHOLDER;
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return String(n);
  }
}

/**
 * Append % without multiplying by 100 (matches PropertyReport / calculateRentability).
 * @param {unknown} value
 * @param {string} [language]
 */
export function formatAdminPercent(value, language = 'fr') {
  if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
  const n = parseAdminFiniteNumber(value);
  if (n == null) return typeof value === 'string' ? value.trim() : ADMIN_DATE_PLACEHOLDER;
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  try {
    return `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(n)}%`;
  } catch {
    return `${n}%`;
  }
}

/**
 * @param {unknown} value
 * @param {string} [language]
 */
export function formatAdminNumber(value, language = 'fr') {
  if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
  const n = parseAdminFiniteNumber(value);
  if (n == null) return typeof value === 'string' ? value.trim() : ADMIN_DATE_PLACEHOLDER;
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n);
  } catch {
    return String(n);
  }
}

/**
 * Format a known financial field using existing app conventions. Unknown values stay raw.
 * @param {string} key
 * @param {unknown} value
 * @param {string} [language]
 */
export function formatAdminFinancialValue(key, value, language = 'fr') {
  if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
  if (key === 'assumptionTermEnd') return formatAdminDate(value, language);
  if (key === 'financingType') {
    const raw = typeof value === 'string' ? value.trim() : '';
    return raw || ADMIN_DATE_PLACEHOLDER;
  }
  if (MONEY_KEYS.has(key)) return formatAdminMoney(value, language);
  if (PERCENT_KEYS.has(key)) return formatAdminPercent(value, language);
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'string') return value.trim() || ADMIN_DATE_PLACEHOLDER;
  return formatAdminNumber(value, language);
}

/**
 * Translate a known enum; otherwise show the raw value.
 * @param {string} prefix
 * @param {unknown} value
 * @param {(key: string) => string} t
 */
export function adminEnumLabel(prefix, value, t) {
  const raw = trimText(value);
  if (!raw) return ADMIN_DATE_PLACEHOLDER;
  const key = `${prefix}.${raw}`;
  const label = t(key);
  return label === key ? raw : label;
}
